import React, { useState } from "react";
import { Button, Modal, Form, Row, Col } from "react-bootstrap";
import {defineMessages, injectIntl} from "react-intl";
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { validate } from "../../../../services/helpers";
import AlertDialog from "../../../../components/AlertDialog";
import useJob from "../../../../hooks/useJob";

// This view's strings for translation
const messages = defineMessages({
    backToInvoice: {
        id: "Palaa laskun tietoihin",
        defaultMessage: "Palaa laskun tietoihin"
    },
    title: {
        id: "Paloturvallisuuden varmistaminen",
        defaultMessage: "Paloturvallisuuden varmistaminen"
    },
    paragraph1: {
        id: "Vahvistan, ettei käyttöpaikallani ole ennen energianjakelun jälleenkytkentää päällä olevia sähkölaitteita, jotka voivat aiheuttaa tulipalon vaaran. Tällaisia laitteita ovat esim. liesi, kiuas, uuni ja voimakkaat remonttivalaisimet. Ilmoitathan asiakaspalveluumme, mikäli mittarilaitteelle ei ole esteetöntä pääsyä.",
        defaultMessage: "Vahvistan, ettei käyttöpaikallani ole ennen energianjakelun jälleenkytkentää päällä olevia sähkölaitteita, jotka voivat aiheuttaa tulipalon vaaran. Tällaisia laitteita ovat esim. liesi, kiuas, uuni ja voimakkaat remonttivalaisimet. Ilmoitathan asiakaspalveluumme, mikäli mittarilaitteelle ei ole esteetöntä pääsyä."
    },
    phonetext: {
        id: "Syötäthän puhelinnumerosi tähän, jotta sähköyhtiösi voi tarvittaessa olla sinuun yhteydessä jälleenkytkentään liittyen.",
        defaultMessage: "Syötäthän puhelinnumerosi tähän, jotta sähköyhtiösi voi tarvittaessa olla sinuun yhteydessä jälleenkytkentään liittyen."
    },
    phone: {
        id: "Puhelinnumero",
        defaultMessage: "Puhelinnumero"
    },
    actionSave: {
        id: "Tallenna",
        defaultMessage: "Tallenna"
    },
    actionSaving: {
        id: "Tallennetaan tietoja...",
        defaultMessage: "Tallennetaan tietoja..."
    },
    titleSavingFailed: {
        id: 'Tallennus epäonnistui',
        defaultMessage: 'Tallennus epäonnistui'
    },
    pleaseTryAgain: {
        id: 'Ole hyvä ja yritä uudelleen.',
        defaultMessage: 'Ole hyvä ja yritä uudelleen.'
    }
});

export { messages };


const FireSafetyModal = (props) => {
    const lng = props.intl.formatMessage;
    const { postFireSafetyEvent } = useJob(props.job);
    const [telephoneNumber, setTelephoneNumber] = useState();
    const [showAlert, setShowAlert] = useState(false);
    const [alert, setAlert] = useState({
        message: '',
        type: ''
    });

    /**
     * Handle changes in input field and remove errors.
     *
     * @param e
     */
    const handleInputUpdate = (e) => {
        // remove validation errors
        if (e.target.checkValidity()) {
            e.target.classList.remove("is-invalid");
        }
        // updates telephone number
        setTelephoneNumber(e.target.value);
    }

    /**
     * Hide alertDialog and clear errors
     */
    const clearAlerts = () => {
        setShowAlert(false);
        setAlert({
            message: '',
            type: ''
        });
    }

    /**
     * Validate form data, then post the fire safety event with the telephone number provided by the user.
     * On success, close the modal and let the caller continue (e.g. proceed to payment).
     * On failure, show an alert in the modal without closing it.
     *
     * @param e
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate(e.currentTarget)) {
            return;
        }

        // clear alerts when data is valid
        clearAlerts();
        props.toggleLoading(true, lng(messages.actionSaving));

        try {
            await postFireSafetyEvent(telephoneNumber);
            props.onClose();
            props.onConfirmed();
        } catch {
            setAlert({
                message: lng(messages.titleSavingFailed) + ". " + lng(messages.pleaseTryAgain),
                type: 'danger'
            });
            setShowAlert(true);
        } finally {
            props.toggleLoading(false);
        }
    }

    /**
     *  Clear errors and hide the dialog
     */
    const handleHide = () => {
        clearAlerts();
        props.onClose();
    }

    return (
        <Modal show={props.show} onHide={handleHide}>
            <Modal.Header closeButton>
                <Button className="modal-back" onClick={handleHide}>
                    <FontAwesomeIcon icon={far.faArrowLeft} /> {lng(messages.backToInvoice)}
                </Button>
                <Modal.Title>{lng(messages.title)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col>
                        <p>{lng(messages.paragraph1)}</p>
                        <p>{lng(messages.phonetext)}</p>
                        {showAlert && (
                            <AlertDialog
                                message={alert.message}
                                type={alert.type}
                            />
                        )}
                        <Form id="firesafety-form" noValidate aria-label={lng(messages.title)} onSubmit={handleSubmit}>
                            <Form.Floating className="mb-3">
                                <Form.Control id="phone" type="text" placeholder={lng(messages.phone)} onChange={(e) => handleInputUpdate(e)} required />
                                <Form.Label htmlFor="phone">{lng(messages.phone)}</Form.Label>
                                <Form.Control.Feedback type="invalid" />
                            </Form.Floating>
                            <Button form="firesafety-form" variant="primary" type="submit">
                                {lng(messages.actionSave)}
                            </Button>
                        </Form>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    );
}

FireSafetyModal.propTypes = {
    'intl': PropTypes.object.isRequired,
    'show': PropTypes.bool,
    'onClose': PropTypes.func,
    'onConfirmed': PropTypes.func,
    'job': PropTypes.object,
    'toggleLoading': PropTypes.func
};

export default injectIntl(FireSafetyModal);
